import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import NotificationBell from '../components/NotificationBell';
import { 
  ArrowLeft,
  Share2,
  Copy,
  Check,
  Mail,
  Linkedin,
  Twitter,
  Users,
  UserPlus,
  UserCheck,
  Award,
  Loader2,
  Send,
  Heart
} from 'lucide-react';
import { toast } from 'sonner';

const LINKEDIN_SHARE_TEXT = `I'm using Varyynt to find my next role in life sciences. It's an AI-powered platform that matches you with opportunities based on your career aspirations, not just your resume. Check it out!`;

const TWITTER_SHARE_TEXT = `Just joined @VaryyntAI - an AI-powered career platform for life sciences professionals. Finding roles that match where I want to go, not just where I've been. Join me!`;

export default function ReferralHubPage() {
  const { authAxios, profile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [referralInfo, setReferralInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [emails, setEmails] = useState('');
  const [sending, setSending] = useState(false);

  const loadReferralInfo = useCallback(async () => {
    try {
      const response = await authAxios.get('/candidates/referral/info');
      setReferralInfo(response.data);
    } catch (error) {
      console.error('Failed to load referral info:', error);
      toast.error('Failed to load referral information');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    loadReferralInfo();
  }, [loadReferralInfo]);

  const copyToClipboard = async () => {
    if (!referralInfo?.referral_url) return;
    
    try {
      await navigator.clipboard.writeText(referralInfo.referral_url);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const shareLinkedIn = () => {
    const url = encodeURIComponent(referralInfo?.referral_url || '');
    const text = encodeURIComponent(LINKEDIN_SHARE_TEXT);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  const shareTwitter = () => {
    const url = encodeURIComponent(referralInfo?.referral_url || '');
    const text = encodeURIComponent(TWITTER_SHARE_TEXT);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  const shareEmail = () => {
    const subject = encodeURIComponent("Join me on Varyynt");
    const body = encodeURIComponent(`Hi,\n\nI've been using Varyynt to explore career opportunities in life sciences, and I thought you might find it valuable too.\n\nIt's an AI-powered platform that matches you with roles based on your career aspirations, not just your resume.\n\nJoin using my link: ${referralInfo?.referral_url}\n\nBest,\n${referralInfo?.first_name || ''}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const sendInvites = async () => {
    if (!emails.trim()) {
      toast.error('Please enter at least one email address');
      return;
    }

    setSending(true);
    try {
      const response = await authAxios.post('/candidates/referral/invite', { emails });
      const results = response.data.results || [];
      
      const invited = results.filter(r => r.status === 'invited').length;
      const alreadyRegistered = results.filter(r => r.status === 'already_registered').length;
      const alreadyInvited = results.filter(r => r.status === 'already_invited').length;
      
      if (invited > 0) {
        toast.success(`${invited} invite${invited > 1 ? 's' : ''} sent successfully!`);
        setEmails('');
        loadReferralInfo();
      }
      if (alreadyRegistered > 0) {
        toast.info(`${alreadyRegistered} email${alreadyRegistered > 1 ? 's are' : ' is'} already registered`);
      }
      if (alreadyInvited > 0) {
        toast.info(`${alreadyInvited} email${alreadyInvited > 1 ? 's were' : ' was'} already invited`);
      }
    } catch (error) {
      console.error('Failed to send invites:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invites');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/settings')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <Link to="/" className="text-xl font-heading text-slate-900">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
            </div>
            <NotificationBell />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-lime-400 to-lime-600 mb-4">
            <Heart className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-heading text-slate-900 mb-2">Help Your Network</h1>
          <p className="text-slate-600 max-w-md mx-auto">
            Know colleagues who are exploring new opportunities? Share Varyynt with them.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 text-slate-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{referralInfo?.stats?.total_invites || 0}</p>
              <p className="text-xs text-slate-500">Invites Sent</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <UserPlus className="h-6 w-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{referralInfo?.stats?.signed_up || 0}</p>
              <p className="text-xs text-slate-500">Signed Up</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <UserCheck className="h-6 w-6 text-lime-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{referralInfo?.stats?.verified || 0}</p>
              <p className="text-xs text-slate-500">Verified</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Award className="h-6 w-6 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-900">{referralInfo?.stats?.onboarded || 0}</p>
              <p className="text-xs text-slate-500">Onboarded</p>
            </CardContent>
          </Card>
        </div>

        {/* Share Link Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Share2 className="h-5 w-5 text-lime-600" />
              Your Personal Invite Link
            </CardTitle>
            <CardDescription>
              Share this link with colleagues - they'll be connected to you when they sign up
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-4">
              <Input 
                value={referralInfo?.referral_url || ''} 
                readOnly 
                className="bg-slate-50 font-mono text-sm"
              />
              <Button onClick={copyToClipboard} variant="outline" className="shrink-0">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button onClick={shareLinkedIn} className="bg-[#0A66C2] hover:bg-[#004182]">
                <Linkedin className="h-4 w-4 mr-2" />
                Share on LinkedIn
              </Button>
              <Button onClick={shareTwitter} variant="outline">
                <Twitter className="h-4 w-4 mr-2" />
                Share on X
              </Button>
              <Button onClick={shareEmail} variant="outline">
                <Mail className="h-4 w-4 mr-2" />
                Email Link
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Email Invite Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="h-5 w-5 text-lime-600" />
              Invite via Email
            </CardTitle>
            <CardDescription>
              Enter email addresses separated by commas (max 10 at a time)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Textarea 
                placeholder="colleague1@company.com, colleague2@company.com"
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                rows={3}
              />
              <Button 
                onClick={sendInvites} 
                disabled={sending || !emails.trim()}
                className="bg-evergreen-950 hover:bg-evergreen-900"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Invites
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
